import React, { useRef } from 'react';
import { CSVLink } from 'react-csv';
import { SDialog, STableContainer, SBadge } from '@avalara/skylab-sdk/react';
import DocumentItemPreview from './DocumentItemPreview';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import { sanitizeCsvData } from '../../../utils/csvSanitizer';
import { resolveStatusKey, resolveInvoiceStatusKey, getProcessingStatusLabel, getInvoiceStatusLabel } from '../../../utils/uploadedDocumentsHelper';

const TITLE_KEY_BY_ACTION = {
    export: 'uploadedDocuments_confirmExportTitle',
    delete: 'uploadedDocuments_confirmDeleteTitle',
    redeliver: 'uploadedDocuments_confirmRedeliverTitle'
};

const buildHeaders = (languages) => ([
    { key: 'fileName', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_fileName') },
    { key: 'processingDate', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_dateProcessed') },
    { key: 'userCode', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_companyCode') },
    { key: 'userName', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_companyName') },
    { key: 'processedCount', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_processed') },
    { key: 'errorCount', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_error') },
    { key: 'onholdCount', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_onHold') },
    { key: 'inboxCount', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_pending') },
    { key: 'totalCount', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_totalNumber') },
    { key: 'processingStatusString', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_fileStatus') },
    { key: 'invoiceStatusString', label: LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_invoiceStatus') }
]);

const buildExportRow = (entry, languages) => {
    const totalCount = (entry.processedCount || 0) + (entry.errorCount || 0) + (entry.onholdCount || 0) + (entry.inboxCount || 0);
    const userName = entry.userName ? entry.userName : 'Unknown company id: ' + entry.userId;
    let processingDate = entry.processingDate;
    if (processingDate === null || processingDate === undefined) {
        processingDate = '-';
    } else if (typeof processingDate !== 'string') {
        processingDate = LANGUAGES_CONTROLLER.format(languages, 'Unknown');
    } else {
        processingDate = processingDate.substring(0, 10) || '-';
    }
    const processingStatusString = getProcessingStatusLabel(languages, resolveStatusKey(entry.processingStatus));
    const invoiceStatusString = getInvoiceStatusLabel(languages, resolveInvoiceStatusKey(entry.invoiceStatus));
    return Object.assign({}, entry, { totalCount, userName, processingDate, processingStatusString, invoiceStatusString });
};

export const BulkOperationConfirmationModal = ({
    isOpen,
    action,
    documents,
    filename,
    languages,
    onRequestAction,
    onConfirm,
    onCancel
}) => {
    const confirmedRef = useRef(false);

    const handleDismiss = () => {
        if (confirmedRef.current) {
            confirmedRef.current = false;
            return;
        }
        onCancel && onCancel();
    };

    const handleConfirmClick = () => {
        confirmedRef.current = true;
        if (action !== 'export' && onRequestAction) {
            const ids = documents.map((document) => ({ id: document.id, fileName: document.fileName }));
            onRequestAction(action, ids);
        }
        onConfirm && onConfirm();
    };

    const isExport = action === 'export';
    const fileNameWithExt = `${filename}.csv`;
    const exportRows = isExport ? documents.map((entry) => buildExportRow(entry, languages)) : [];

    return (
        <SDialog
            open={isOpen}
            onS-dismiss={handleDismiss}
            className="uploaded-documents-confirmation-dialog"
            data-testid="bulk-operation-confirmation-modal"
        >
            <div slot="header">
                <h1><LanguageStringResolver string={TITLE_KEY_BY_ACTION[action]} /></h1>
            </div>
            <div slot="body">
                <STableContainer>
                    <table id="bulk-operation-confirmation-table" className="uploaded-documents-table">
                        <thead>
                            <DocumentItemPreview header={true} />
                        </thead>
                        <tbody>
                            {documents.map((document, index) => (
                                <DocumentItemPreview key={document.id ?? index} document={document} languages={languages} />
                            ))}
                        </tbody>
                    </table>
                </STableContainer>
            </div>
            <div slot="footer" className="justify-content-space align-items-center">
                <div className="flex align-items-center" style={{ gap: '0.5rem' }}>
                    <span><LanguageStringResolver string="uploadedDocuments_confirmationDocumentsSelected" /></span>
                    <SBadge count={documents.length} />
                </div>
                <div className="flex align-items-center" style={{ gap: '0.5rem' }}>
                    {isExport ? (
                        <CSVLink
                            data={sanitizeCsvData(exportRows)}
                            filename={fileNameWithExt}
                            headers={buildHeaders(languages)}
                            className="button primary"
                            onClick={handleConfirmClick}
                        >
                            <LanguageStringResolver string="uploadedDocuments_confirmActionButton" />
                        </CSVLink>
                    ) : (
                        <button type="button" className="primary" onClick={handleConfirmClick}>
                            <LanguageStringResolver string="uploadedDocuments_confirmActionButton" />
                        </button>
                    )}
                    <button type="button" className="secondary" onClick={handleDismiss}>
                        <LanguageStringResolver string="confirmationModal_cancel" />
                    </button>
                </div>
            </div>
        </SDialog>
    );
};


BulkOperationConfirmationModal.defaultProps = {
    isOpen: false,
    documents: [],
    filename: 'export',
    languages: {}
};

export default BulkOperationConfirmationModal;
